import React, { useEffect, useState } from "react";
import {
    Form, FormGroup, Label, Input, Row, Col, Button, FormFeedback
} from "reactstrap";
import Session from '../../auth/Session';

const InvoiceAdjustmentForm = ({ invoiceId, adjustment, routes = [], onSuccess, onCancel }) => {
    const isEdit = !!adjustment;
    const [formData, setFormData] = useState({
        description: adjustment?.description ?? "",
        amount: adjustment != null && adjustment.lineTotal != null ? `${Math.abs(adjustment.lineTotal)}` : "",
        quantity: adjustment != null && adjustment.daysOnRoute != null ? `${adjustment.daysOnRoute}` : "1",
        transactionTypeId: adjustment?.transactionTypeId != null ? `${adjustment.transactionTypeId}` : "",
        routeId: adjustment?.routeId != null ? `${adjustment.routeId}` : "",
    });
    const [transactionTypes, setTransactionTypes] = useState([]);
    // Fall back to the routes already on the invoice until the full active-route list loads.
    const [routeOptions, setRouteOptions] = useState(routes);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const load = async () => {
            const token = await Session.getToken();
            const headers = { Authorization: `Bearer ${token}` };
            const [typesRes, routesRes] = await Promise.all([
                fetch("/api/invoicing/transaction-types", { headers }),
                fetch(`/api/invoicing/${invoiceId}/routes`, { headers }),
            ]);
            if (typesRes.ok) {
                setTransactionTypes(await typesRes.json());
            }
            // All active routes for the invoice's sub-customer, so a route that was never
            // imported (e.g. a manually billed "Stops" route) can still be selected.
            if (routesRes.ok) {
                setRouteOptions(await routesRes.json());
            }
        };
        load();
    }, [invoiceId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(p => ({ ...p, [name]: value }));
    };

    const validate = () => {
        const v = {};
        if (!formData.description.trim()) v.description = "Description is required";
        const amount = Number(formData.amount);
        if (!formData.amount || isNaN(amount) || amount <= 0) v.amount = "Amount must be greater than zero";
        const quantity = Number(formData.quantity);
        if (!formData.quantity || isNaN(quantity) || quantity <= 0) v.quantity = "Quantity must be greater than zero";
        if (!formData.transactionTypeId) v.transactionTypeId = "Transaction type is required";
        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const token = await Session.getToken();
            const url = isEdit ? "/api/invoicing/update-adjustment" : "/api/invoicing/add-adjustment";
            const routeId = formData.routeId ? Number(formData.routeId) : null;
            const body = isEdit
                ? {
                    invoiceId: invoiceId,
                    invoiceDetailId: adjustment.id,
                    description: formData.description.trim(),
                    amount: Number(formData.amount),
                    quantity: Number(formData.quantity),
                    transactionTypeId: Number(formData.transactionTypeId),
                    routeId: routeId,
                }
                : {
                    invoiceId: invoiceId,
                    description: formData.description.trim(),
                    amount: Number(formData.amount),
                    quantity: Number(formData.quantity),
                    transactionTypeId: Number(formData.transactionTypeId),
                    routeId: routeId,
                };
            const res = await fetch(url, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const t = await res.text();
                alert(`Save failed: ${res.status} ${t}`);
                return;
            }
            onSuccess(await res.json());
        } finally {
            setSubmitting(false);
        }
    };

    const selectedType = transactionTypes.find(t => `${t.id}` === `${formData.transactionTypeId}`);

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label>Route</Label>
                        <Input
                            name="routeId"
                            type="select"
                            value={formData.routeId}
                            onChange={handleChange}
                        >
                            <option value="">— none (invoice-level) —</option>
                            {routeOptions.map(r => (
                                <option key={r.routeId} value={r.routeId}>{r.name}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label>Description</Label>
                        <Input
                            name="description"
                            type="text"
                            maxLength={500}
                            value={formData.description}
                            onChange={handleChange}
                            invalid={!!errors.description}
                        />
                        <FormFeedback>{errors.description}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>Quantity</Label>
                        <Input
                            name="quantity"
                            type="number"
                            step="any"
                            min="0"
                            value={formData.quantity}
                            onChange={handleChange}
                            invalid={!!errors.quantity}
                        />
                        <FormFeedback>{errors.quantity}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Amount</Label>
                        <Input
                            name="amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.amount}
                            onChange={handleChange}
                            invalid={!!errors.amount}
                        />
                        <FormFeedback>{errors.amount}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Transaction Type</Label>
                        <Input
                            name="transactionTypeId"
                            type="select"
                            value={formData.transactionTypeId}
                            onChange={handleChange}
                            invalid={!!errors.transactionTypeId}
                        >
                            <option value="">-- Transaction Type --</option>
                            {transactionTypes.map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.transactionName}{t.isPositiveAdjustment === false ? " (deduction)" : ""}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.transactionTypeId}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            {selectedType && (
                <div className="text-muted small mb-3">
                    This will be recorded as {selectedType.isPositiveAdjustment === false ? "a deduction (negative)" : "an addition (positive)"} on the invoice.
                </div>
            )}
            <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" type="button" onClick={onCancel} disabled={submitting}>Cancel</Button>
                <Button color="primary" type="submit" disabled={submitting}>
                    {submitting ? "Saving…" : isEdit ? "Save Adjustment" : "Add Adjustment"}
                </Button>
            </div>
        </Form>
    );
};

export default InvoiceAdjustmentForm;
